import { defineChart } from '..'
import { tooltipWithTitle } from '@/composables/useChart/theme'

type Status = { label: string; color: string; value: number }
type Params = [string, Value[], Dimension[], Status[], boolean, boolean?]

export default defineChart<Params>(
    (title, values, children, statuses, condensed, isIndicator) => {
        if (!values.length || !children.length || !statuses.length) return {}

        /**
         * Use params.
         */
        const { config } = useParams()
        /**
         * List of countries
         */
        const countries = [...new Set(values.map(({ iso }) => iso))].sort(a =>
            a === config.country ? -1 : 1
        )

        /**
         * Data for the chart.
         */
        const data = values.map(value => {
            const index = children.findIndex(child => child.id === value.id)
            const dimension = children[index]
            const statusValue = dimension?.negative
                ? isIndicator
                    ? value.value === 0
                        ? 1
                        : 0
                    : -value.value
                : value.value

            return {
                value: [value.iso, value.id, value.value] as [
                    string,
                    string,
                    number
                ],
                itemStyle: {
                    color:
                        dimension?.negative && !value.value
                            ? twColor('gray', 50)
                            : getStatus(statuses, statusValue)?.color
                },
                index,
                sortIndex: dimension?.negative ? -index : index,
                statusValue,
                id: value.id
            }
        })

        const sortedLabels = data
            .filter(({ value }) => value[0] === config.country)
            .sort((a, b) => {
                if (isIndicator) {
                    return a.value[2] === b.value[2]
                        ? a.sortIndex - b.sortIndex // sort by index
                        : a.value[2] - b.value[2] // sort by value
                }

                return a.value[2] === b.value[2]
                    ? a.sortIndex - b.sortIndex // sort by index
                    : a.statusValue - b.statusValue // sort by value * negative
            })
            .map(({ value }) => value[1])

        return {
            tooltip: {},
            title: {
                text: title,
                left: 'center',
                textStyle: {
                    fontSize: 14,
                    fontWeight: 'bold',
                    width: 500,
                    overflow: 'break'
                }
            },
            grid: {
                top: isIndicator ? '10%' : '14%',
                left: '2.5%',
                right: '2.5%',
                bottom: '2.5%',
                containLabel: true
            },
            export: {
                title: {
                    text: title
                },
                grid: {
                    top: isIndicator ? '10%' : '14%',
                    bottom: '10%'
                },
                yAxis: {
                    axisLabel: {
                        width: 700
                    }
                }
            },
            xAxis: {
                data: countries,
                type: 'category',
                position: 'top',
                show: countries.length > 1,
                axisTick: { show: false },
                axisLine: { show: false },
                z: 1000,
                axisLabel: {
                    show: true,
                    fontWeight: 'bold'
                }
            },
            yAxis: {
                data: sortedLabels,
                type: 'category',
                position: 'right',
                show: true,
                axisLine: {
                    show: false
                },
                axisTick: {
                    show: false
                },
                axisLabel: {
                    rich: {
                        negative: {
                            color: 'black',
                            backgroundColor: twColor('earth-red', 50),
                            borderColor: twColor('earth-red'),
                            borderWidth: 0.5,
                            fontWeight: 'bold'
                        }
                    },
                    formatter(value) {
                        const dimension = children.find(
                            ({ id }) => id === value
                        )

                        if (!dimension) return ''

                        if (dimension.negative)
                            return `{negative|NEGATIVE} ${dimension.name}`

                        return `${dimension.name}`
                    },
                    show: true
                }
            },
            visualMap: {
                show: !isIndicator,
                type: 'piecewise',
                selectedMode: false,
                orient: 'horizontal',
                top: '7%',
                inverse: true,
                pieces: statuses.map(status => ({
                    label: status.label,
                    value: status.value,
                    color: status.color
                }))
            },
            series: {
                type: 'heatmap',
                tooltip: {
                    formatter(params) {
                        const value = params.value as [string, string, number]
                        const country = value[0]
                        const dimension = children.find(
                            child => child.id === value[1]
                        ) as Dimension
                        const status = getStatus(statuses, value[2])
                            ?.label as string

                        return tooltipWithTitle(dimension.name, country, status)
                    }
                },
                itemStyle: {
                    borderColor: 'white',
                    borderWidth: 2
                },
                label: {
                    show: isIndicator,
                    fontSize: condensed ? 8 : 11,
                    formatter(params) {
                        const value = params.value as [string, string, number]
                        const status = getStatus(statuses, value[2])
                            ?.label as string

                        return status
                    }
                },
                data
            },
            media: [
                {
                    query: {
                        maxWidth: 300
                    },
                    option: {
                        title: {
                            show: false
                        },
                        grid: {
                            top: 0
                        },
                        visualMap: {
                            show: false
                        },
                        yAxis: {
                            axisLabel: {
                                overflow: 'truncate',
                                show: false,
                                fontSize: 8,
                                lineHeight: 10,
                                width: 150,
                                margin: 5,
                                rich: {
                                    negative: {
                                        padding: [2, 5, 1, 5],
                                        fontSize: 8
                                    }
                                }
                            }
                        }
                    }
                },
                {
                    query: {
                        minWidth: 300
                    },
                    option: {
                        title: {
                            show: !condensed,
                            textStyle: {
                                width: 350,
                                fontSize: 12
                            }
                        },
                        grid: {
                            top: condensed ? 0 : isIndicator ? '7.5%' : '17%'
                        },
                        visualMap: {
                            show: !isIndicator && !condensed,
                            top: '13%',
                            textStyle: {
                                overflow: 'break',
                                fontSize: 8
                            },
                            itemHeight: 10,
                            itemWidth: 10
                        },
                        yAxis: {
                            axisLabel: {
                                overflow: condensed ? 'truncate' : 'break',
                                show: true,
                                fontSize: 11,
                                lineHeight: 13,
                                width: condensed ? 200 : 350,
                                margin: 10,
                                rich: {
                                    negative: {
                                        padding: [2, 6, 1, 6],
                                        fontSize: 9
                                    }
                                }
                            }
                        }
                    }
                },
                {
                    query: {
                        minWidth: 500
                    },
                    option: {
                        title: {
                            show: !condensed,
                            textStyle: {
                                width: 500,
                                fontSize: 14
                            }
                        },
                        grid: {
                            top: condensed ? 0 : isIndicator ? '15%' : '21%'
                        },
                        visualMap: {
                            show: !isIndicator && !condensed,
                            textStyle: {
                                fontSize: 12
                            },
                            itemHeight: 14,
                            itemWidth: 14
                        },
                        yAxis: {
                            axisLabel: {
                                overflow: condensed ? 'truncate' : 'break',
                                show: true,
                                fontSize: 13,
                                lineHeight: 16,
                                width: condensed ? 200 : 500,
                                margin: 20,
                                rich: {
                                    negative: {
                                        padding: [3, 8, 2, 8],
                                        fontSize: 10
                                    }
                                }
                            }
                        }
                    }
                }
            ]
        }
    }
)

/**
 * Get status by value.
 */
function getStatus(statuses: Status[], value: number) {
    return statuses.find(status => status.value === value)
}
